import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useContract, useUpdateVolumeDistribution } from '@/hooks/useContracts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Building2, Package, Calendar, Truck, Loader2, CheckCircle, XCircle, FileCheck, Edit, Check, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useState } from 'react';
import { formatNumber } from '@/lib/numberFormat';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { VolumeDistribution } from '@/types';

export default function ContractDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: contract, isLoading, error } = useContract(parseInt(id || '0'));
  const updateVolumeDistribution = useUpdateVolumeDistribution();

  // State for editing confirmedBy field
  const [editingConfirmedByVdId, setEditingConfirmedByVdId] = useState<number | null>(null);
  const [confirmedByValue, setConfirmedByValue] = useState('');

  const isAdmin = user?.role === 'Admin' || false;
  const isManager = user?.role === 'BU Manager' || isAdmin || false;
  const userBusinessUnitId = user?.businessUnitId;

  // Check if user can edit confirmedBy for a given volume distribution
  const canEditConfirmedBy = (vdBusinessUnitId: number) => {
    // Admins can edit any, otherwise user must be assigned to that BU
    return isAdmin || (userBusinessUnitId && parseInt(userBusinessUnitId) === vdBusinessUnitId);
  };

  const handleEditConfirmedBy = (vd: VolumeDistribution) => {
    setEditingConfirmedByVdId(vd.id);
    setConfirmedByValue(vd.confirmedBy || '');
  };

  const handleSaveConfirmedBy = async (vdId: number) => {
    await updateVolumeDistribution.mutateAsync({
      id: vdId,
      data: { confirmedBy: confirmedByValue || undefined },
    });
    setEditingConfirmedByVdId(null);
    setConfirmedByValue('');
  };

  const handleCancelEdit = () => {
    setEditingConfirmedByVdId(null);
    setConfirmedByValue('');
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (error || !contract) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold">Contract not found</h1>
          <Link to="/contracts">
            <Button className="mt-4">Back to Contracts</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <Link to="/contracts">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Contracts
            </Button>
          </Link>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold">{contract.contractName}</h1>
              <p className="text-muted-foreground mt-1">
                {contract.productName} • {contract.supplierName}
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Contract Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contract Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Contract Name</p>
                <p className="text-sm mt-1 font-semibold">{contract.contractName}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Contract Date</p>
                  <p className="text-sm mt-1">
                    {new Date(contract.contractDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Contract Type</p>
                  <p className="text-sm mt-1 capitalize">
                    {typeof contract.contractType === 'string' ? contract.contractType.replace('_', ' ') : contract.contractType}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Start Date</p>
                  <p className="text-sm mt-1">
                    {new Date(contract.startDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">End Date</p>
                  <p className="text-sm mt-1">
                    {new Date(contract.endDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Volume</p>
                              <p className="text-sm mt-1 font-semibold">
                                  {formatNumber(contract.totalVolume, 2)} kgs ({formatNumber(contract.totalVolume / 1000, 2)} tons)
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Created By</p>
                <p className="text-sm mt-1">
                  {contract.createdBy} on {new Date(contract.createdAt).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Business Unit Assignment */}
          <Card>
            <CardHeader>
              <CardTitle>Business Unit Assignment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Mode</p>
                <p className="text-sm mt-1 capitalize">{contract.businessUnitMode} Business Unit</p>
              </div>
              {contract.businessUnitMode === 'Single' && contract.volumeDistributions?.[0] && (
                <>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Business Unit</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Building2 className="h-4 w-4 text-primary" />
                      <p className="text-sm font-semibold">
                        {contract.volumeDistributions[0].businessUnitName}
                      </p>
                    </div>
                  </div>
                
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Price per Kg</p>
                      <p className="text-sm mt-1 font-semibold">
                        {formatNumber(contract.volumeDistributions[0].pricePerKg, 3)} {contract.volumeDistributions[0].currency}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Confirmed By</p>
                      {editingConfirmedByVdId === contract.volumeDistributions[0].id ? (
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            value={confirmedByValue}
                            onChange={(e) => setConfirmedByValue(e.target.value)}
                            placeholder="Enter name"
                            className="h-8 w-40"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => handleSaveConfirmedBy(contract.volumeDistributions[0].id)}
                            disabled={updateVolumeDistribution.isPending}
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={handleCancelEdit}
                          >
                            <X className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm">{contract.volumeDistributions[0].confirmedBy || 'N/A'}</p>
                          {canEditConfirmedBy(contract.volumeDistributions[0].businessUnitId) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0"
                              onClick={() => handleEditConfirmedBy(contract.volumeDistributions[0])}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
              {contract.businessUnitMode === 'Several' && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Distribution Count</p>
                  <p className="text-sm mt-1 font-semibold">
                    {contract.volumeDistributions?.length || 0} business units
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Shipping Instructions */}
        {contract.volumeDistributions?.[0]?.shippingInstructions && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Shipping Instructions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{contract.volumeDistributions[0].shippingInstructions}</p>
            </CardContent>
          </Card>
        )}

        {/* Volume Distributions Table - Now shown for BOTH modes */}
        {contract.volumeDistributions && contract.volumeDistributions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                Volume Distributions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business Unit</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Volume</TableHead>
                    <TableHead>Inco Term</TableHead>
                    <TableHead>Inco Term Place</TableHead>
                    <TableHead className="text-right">Price/Kg</TableHead>
                    <TableHead>Confirmed By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contract.volumeDistributions.map((vd) => (
                    <TableRow key={vd.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{vd.businessUnitName}</p>
                        </div>
                      </TableCell>
                      <TableCell>{vd.productName}</TableCell>
                      <TableCell>{vd.supplierName}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatNumber(vd.volumeKgs, 2)} kgs
                      </TableCell>
                      <TableCell>
                        {vd.incoTerm}
                      </TableCell>
                      <TableCell>
                        {vd.incoTermPlace}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(vd.pricePerKg, 3)} {vd.currency}
                      </TableCell>
                      <TableCell>
                        {editingConfirmedByVdId === vd.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              value={confirmedByValue}
                              onChange={(e) => setConfirmedByValue(e.target.value)}
                              placeholder="Name"
                              className="h-7 w-28 text-sm"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => handleSaveConfirmedBy(vd.id)}
                              disabled={updateVolumeDistribution.isPending}
                            >
                              <Check className="h-3 w-3 text-green-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={handleCancelEdit}
                            >
                              <X className="h-3 w-3 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span>{vd.confirmedBy || 'N/A'}</span>
                            {canEditConfirmedBy(vd.businessUnitId) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0"
                                onClick={() => handleEditConfirmedBy(vd)}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
